import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Eye,
  FilePlus2,
  Plus,
  Save,
  Send,
  Trash2,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import SEO from "@/components/site/SEO";
import lmsApi from "@/lib/lmsApi";

const ACTIVITY_TYPES = [
  ["content", "Learning content"],
  ["video", "Video"],
  ["download", "Download"],
  ["external_link", "External link"],
  ["mcq", "Multiple choice"],
  ["multi_select", "Multiple response"],
  ["true_false", "True / false"],
  ["scenario", "Scenario"],
  ["reflection", "Reflection"],
  ["acknowledgement", "Acknowledgement"],
  ["assessment", "Assessment"],
];

function blankActivity(moduleId) {
  return {
    moduleId,
    activityId: null,
    title: "",
    activity_type: "content",
    main: "",
    options: "",
    required: true,
  };
}

function activityToForm(moduleId, activity) {
  const content = activity.content || {};

  let main = "";
  let options = "";

  if (activity.activity_type === "content") {
    main = content.body || "";
  } else if (
    ["video", "download", "external_link"].includes(
      activity.activity_type
    )
  ) {
    main = content.url || "";
  } else if (activity.activity_type === "scenario") {
    main = content.scenario || "";

    options = (content.options || [])
      .map((item) =>
        typeof item === "string"
          ? item
          : item.label || ""
      )
      .join("\n");
  } else if (
    activity.activity_type === "acknowledgement"
  ) {
    main = content.statement || "";
  } else if (
    activity.activity_type === "reflection"
  ) {
    main = content.prompt || "";
  } else if (
    [
      "mcq",
      "multi_select",
      "true_false",
      "assessment",
    ].includes(activity.activity_type)
  ) {
    const question =
      (content.questions || [])[0] || {};

    main = question.prompt || "";

    options = (question.options || [])
      .map((item) =>
        typeof item === "string"
          ? item
          : item.label || ""
      )
      .join("\n");
  }

  return {
    moduleId,
    activityId: activity.id,
    title: activity.title,
    activity_type: activity.activity_type,
    main,
    options,
    required: activity.required,
  };
}

function buildActivityContent(form) {
  const options = form.options
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);

  switch (form.activity_type) {
    case "content":
      return {
        body: form.main,
      };

    case "video":
    case "download":
    case "external_link":
      return {
        url: form.main,
      };

    case "scenario":
      return {
        scenario: form.main,
        options,
      };

    case "acknowledgement":
      return {
        statement: form.main,
      };

    case "reflection":
      return {
        prompt: form.main,
      };

    case "true_false":
      return {
        questions: [
          {
            prompt: form.main,
            options: ["True", "False"],
          },
        ],
      };

    case "mcq":
    case "multi_select":
    case "assessment":
      return {
        questions: [
          {
            prompt: form.main,
            options,
          },
        ],
      };

    default:
      return {};
  }
}

function activityMainLabel(type) {
  if (
    ["video", "download", "external_link"].includes(
      type
    )
  ) {
    return "URL";
  }

  if (type === "scenario") {
    return "Scenario";
  }

  if (type === "acknowledgement") {
    return "Statement";
  }

  if (type === "reflection") {
    return "Reflection prompt";
  }

  if (
    [
      "mcq",
      "multi_select",
      "true_false",
      "assessment",
    ].includes(type)
  ) {
    return "Question";
  }

  return "Learning content";
}

function needsOptions(type) {
  return [
    "mcq",
    "multi_select",
    "scenario",
    "assessment",
  ].includes(type);
}

function PreviewActivity({ activity }) {
  const content = activity.content || {};

  if (activity.activity_type === "content") {
    return (
      <p className="whitespace-pre-wrap text-slate-700">
        {content.body}
      </p>
    );
  }

  if (
    ["video", "download", "external_link"].includes(
      activity.activity_type
    )
  ) {
    return (
      <p className="break-all text-sm text-slate-500">
        {content.url}
      </p>
    );
  }

  if (activity.activity_type === "scenario") {
    return (
      <div>
        <p className="whitespace-pre-wrap text-slate-700">
          {content.scenario}
        </p>

        <ul className="mt-3 space-y-2">
          {(content.options || []).map(
            (option, index) => (
              <li
                key={index}
                className="rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-600"
              >
                {typeof option === "string"
                  ? option
                  : option.label}
              </li>
            )
          )}
        </ul>
      </div>
    );
  }

  if (
    activity.activity_type ===
    "acknowledgement"
  ) {
    return (
      <p className="text-slate-700">
        {content.statement}
      </p>
    );
  }

  if (activity.activity_type === "reflection") {
    return (
      <p className="text-slate-700">
        {content.prompt}
      </p>
    );
  }

  const question =
    (content.questions || [])[0];

  if (question) {
    return (
      <div>
        <p className="font-semibold text-navy">
          {question.prompt}
        </p>

        <ul className="mt-3 space-y-2">
          {(question.options || []).map(
            (option, index) => (
              <li
                key={index}
                className="rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-600"
              >
                {typeof option === "string"
                  ? option
                  : option.label}
              </li>
            )
          )}
        </ul>
      </div>
    );
  }

  return (
    <p className="text-sm text-slate-500">
      Activity preview will appear here.
    </p>
  );
}

export default function LMSCourseEditor() {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [version, setVersion] = useState(null);

  const [
    selectedVersionId,
    setSelectedVersionId,
  ] = useState("");

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [preview, setPreview] = useState(false);

  const [courseForm, setCourseForm] =
    useState({
      title: "",
      description: "",
    });

  const [versionForm, setVersionForm] =
    useState({
      pass_mark: "",
      estimated_minutes: "",
      cpd_minutes: "",
    });

  const [
    newModuleTitle,
    setNewModuleTitle,
  ] = useState("");

  const [activityForm, setActivityForm] =
    useState(null);

  const loadCourse = async (
    preferredVersionId = null
  ) => {
    setError("");

    try {
      const me = await lmsApi.get("/auth/me");

      if (me.data.require_password_change) {
        navigate("/learn/change-password", {
          replace: true,
        });
        return;
      }

      if (!me.data.is_platform_admin) {
        navigate("/learn", {
          replace: true,
        });
        return;
      }

      const courseResponse =
        await lmsApi.get(
          `/author/courses/${courseId}`
        );

      const nextCourse =
        courseResponse.data;

      setCourse(nextCourse);

      setCourseForm({
        title: nextCourse.title,
        description:
          nextCourse.description || "",
      });

      const chosen =
        nextCourse.versions.find(
          (item) =>
            item.id === preferredVersionId
        ) ||
        nextCourse.versions.find(
          (item) =>
            item.status === "draft"
        ) ||
        nextCourse.versions.find(
          (item) =>
            item.status === "published"
        ) ||
        nextCourse.versions[0];

      if (chosen) {
        const versionResponse =
          await lmsApi.get(
            `/author/courses/${courseId}/versions/${chosen.id}`
          );

        setVersion(versionResponse.data);

        setSelectedVersionId(chosen.id);

        setVersionForm({
          pass_mark:
            versionResponse.data.pass_mark ??
            "",

          estimated_minutes:
            versionResponse.data
              .estimated_minutes ?? "",

          cpd_minutes:
            versionResponse.data.cpd_minutes ??
            "",
        });
      }
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/learn/login", {
          replace: true,
        });
        return;
      }

      setError(
        err.response?.data?.detail ||
          "We could not load this course."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCourse();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const editable =
    version?.status === "draft" &&
    course?.status !== "archived";

  const moduleCount =
    version?.modules?.length || 0;

  const activityCount = useMemo(
    () =>
      (version?.modules || []).reduce(
        (total, module) =>
          total +
          module.activities.length,
        0
      ),
    [version]
  );

  const selectVersion = async (id) => {
    setBusy(true);
    setError("");

    try {
      const response = await lmsApi.get(
        `/author/courses/${courseId}/versions/${id}`
      );

      setVersion(response.data);
      setSelectedVersionId(id);

      setVersionForm({
        pass_mark:
          response.data.pass_mark ?? "",

        estimated_minutes:
          response.data.estimated_minutes ??
          "",

        cpd_minutes:
          response.data.cpd_minutes ?? "",
      });

      setActivityForm(null);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "The version could not be loaded."
      );
    } finally {
      setBusy(false);
    }
  };

  const saveCourse = async () => {
    setBusy(true);
    setError("");
    setNotice("");

    try {
      await lmsApi.patch(
        `/author/courses/${courseId}`,
        {
          title: courseForm.title,
          description:
            courseForm.description,
        }
      );

      setNotice(
        "Course details saved."
      );

      await loadCourse(
        selectedVersionId
      );
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Course details could not be saved."
      );
    } finally {
      setBusy(false);
    }
  };

  const saveVersion = async () => {
    setBusy(true);
    setError("");
    setNotice("");

    try {
      const payload = {};

      if (
        versionForm.pass_mark !== ""
      ) {
        payload.pass_mark = Number(
          versionForm.pass_mark
        );
      }

      if (
        versionForm.estimated_minutes !==
        ""
      ) {
        payload.estimated_minutes =
          Number(
            versionForm.estimated_minutes
          );
      }

      if (
        versionForm.cpd_minutes !== ""
      ) {
        payload.cpd_minutes = Number(
          versionForm.cpd_minutes
        );
      }

      const response =
        await lmsApi.patch(
          `/author/courses/${courseId}/versions/${version.id}`,
          payload
        );

      setVersion(response.data);

      setNotice(
        "Version settings saved."
      );
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Version settings could not be saved."
      );
    } finally {
      setBusy(false);
    }
  };

  const createDraftVersion = async () => {
    setBusy(true);
    setError("");
    setNotice("");

    try {
      const source =
        version?.status === "published"
          ? version.id
          : null;

      const response =
        await lmsApi.post(
          `/author/courses/${courseId}/versions`,
          {
            source_version_id: source,
          }
        );

      setNotice(
        `Draft version ${response.data.version_number} created.`
      );

      await loadCourse(
        response.data.id
      );
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "A new draft version could not be created."
      );
    } finally {
      setBusy(false);
    }
  };

  const addModule = async (event) => {
    event.preventDefault();

    if (!newModuleTitle.trim()) {
      return;
    }

    setBusy(true);
    setError("");

    try {
      await lmsApi.post(
        `/author/courses/${courseId}/versions/${version.id}/modules`,
        {
          title: newModuleTitle,
          required: true,
          completion_rules: {},
        }
      );

      setNewModuleTitle("");

      await loadCourse(version.id);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "The module could not be added."
      );
    } finally {
      setBusy(false);
    }
  };

  const changeModulePosition = async (
    module,
    direction
  ) => {
    const nextPosition = Math.max(
      1,
      module.position + direction
    );

    setBusy(true);

    try {
      await lmsApi.patch(
        `/author/courses/${courseId}/versions/${version.id}/modules/${module.id}`,
        {
          position: nextPosition,
        }
      );

      await loadCourse(version.id);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "The module order could not be changed."
      );
    } finally {
      setBusy(false);
    }
  };

  const deleteModule = async (module) => {
    if (
      !window.confirm(
        `Delete module “${module.title}” and all of its draft activities?`
      )
    ) {
      return;
    }

    setBusy(true);

    try {
      await lmsApi.delete(
        `/author/courses/${courseId}/versions/${version.id}/modules/${module.id}`
      );

      await loadCourse(version.id);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "The module could not be deleted."
      );
    } finally {
      setBusy(false);
    }
  };

  const saveActivity = async (event) => {
    event.preventDefault();

    setBusy(true);
    setError("");

    try {
      const payload = {
        title: activityForm.title,
        activity_type:
          activityForm.activity_type,
        required:
          activityForm.required,
        content:
          buildActivityContent(
            activityForm
          ),
        completion_rules: {},
      };

      const base =
        `/author/courses/${courseId}` +
        `/versions/${version.id}` +
        `/modules/${activityForm.moduleId}` +
        `/activities`;

      if (activityForm.activityId) {
        await lmsApi.patch(
          `${base}/${activityForm.activityId}`,
          payload
        );
      } else {
        await lmsApi.post(
          base,
          payload
        );
      }

      setActivityForm(null);

      await loadCourse(version.id);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "The activity could not be saved."
      );
    } finally {
      setBusy(false);
    }
  };

  const deleteActivity = async (
    module,
    activity
  ) => {
    if (
      !window.confirm(
        `Delete activity “${activity.title}”?`
      )
    ) {
      return;
    }

    setBusy(true);

    try {
      await lmsApi.delete(
        `/author/courses/${courseId}/versions/${version.id}/modules/${module.id}/activities/${activity.id}`
      );

      await loadCourse(version.id);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "The activity could not be deleted."
      );
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (
      !window.confirm(
        `Publish version ${version.version_number}? Once published, this version becomes immutable.`
      )
    ) {
      return;
    }

    setBusy(true);
    setError("");
    setNotice("");

    try {
      await lmsApi.post(
        `/author/courses/${courseId}/versions/${version.id}/publish`
      );

      setNotice(
        `Version ${version.version_number} has been published and locked.`
      );

      await loadCourse(version.id);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "This version could not be published."
      );
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <section className="min-h-screen bg-slate-50 py-20">
        <div className="container-rail text-center text-slate-600">
          Loading course editor…
        </div>
      </section>
    );
  }

  if (!course || !version) {
    return (
      <section className="min-h-screen bg-slate-50 py-20">
        <div className="container-rail text-center text-red-700">
          {error ||
            "Course not found."}
        </div>
      </section>
    );
  }

  return (
    <>
      <SEO
        title={`${course.title} | Course Authoring | TRAC-Learn`}
        description="TRAC-Learn course editor."
        path={`/learn/admin/courses/${courseId}`}
      />

      <section className="bg-navy py-8 text-white md:py-10">
        <div className="container-rail">
          <button
            type="button"
            onClick={() =>
              navigate(
                "/learn/admin/courses"
              )
            }
            className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Course catalogue
          </button>

          <div className="mt-5 flex flex-col justify-between gap-6 xl:flex-row xl:items-end">
            <div>
              <p className="font-mono text-xs text-white/50">
                {course.slug}
              </p>

              <h1 className="mt-2 font-display text-4xl font-bold md:text-5xl">
                {course.title}
              </h1>

              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-white/70">
                <span>
                  Version{" "}
                  {version.version_number}
                </span>

                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                    version.status ===
                    "published"
                      ? "bg-emerald-400/20 text-emerald-200"
                      : "bg-amber-400/20 text-amber-200"
                  }`}
                >
                  {version.status}
                </span>

                <span>
                  {moduleCount} modules
                </span>

                <span>
                  {activityCount} activities
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <select
                value={selectedVersionId}
                onChange={(event) =>
                  selectVersion(
                    event.target.value
                  )
                }
                disabled={busy}
                className="rounded-lg border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold text-white outline-none"
              >
                {course.versions.map(
                  (item) => (
                    <option
                      key={item.id}
                      value={item.id}
                      className="text-slate-900"
                    >
                      Version{" "}
                      {
                        item.version_number
                      }{" "}
                      — {item.status}
                    </option>
                  )
                )}
              </select>

              <button
                type="button"
                onClick={() =>
                  setPreview(
                    (value) => !value
                  )
                }
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/5 px-4 py-3 text-sm font-semibold hover:bg-white/10"
              >
                <Eye className="h-4 w-4" />

                {preview
                  ? "Edit view"
                  : "Preview"}
              </button>

              {!editable &&
                course.status !==
                  "archived" && (
                  <button
                    type="button"
                    onClick={
                      createDraftVersion
                    }
                    disabled={
                      busy ||
                      course.latest_draft_version !==
                        null
                    }
                    className="inline-flex items-center gap-2 rounded-lg bg-safety px-4 py-3 text-sm font-bold disabled:opacity-50"
                  >
                    <FilePlus2 className="h-4 w-4" />
                    New draft version
                  </button>
                )}

              {editable && (
                <button
                  type="button"
                  onClick={publish}
                  disabled={busy}
                  className="inline-flex items-center gap-2 rounded-lg bg-safety px-4 py-3 text-sm font-bold disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                  Publish version
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="min-h-[70vh] bg-slate-50 py-8 md:py-12">
        <div className="container-rail">
          {error && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {error}
            </div>
          )}

          {notice && (
            <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
              {notice}
            </div>
          )}

          {preview ? (
            <div className="mx-auto max-w-4xl">
              <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm md:p-10">
                <p className="label-eyebrow text-safety">
                  Learner preview
                </p>

                <h2 className="mt-3 font-display text-4xl font-bold text-navy">
                  {course.title}
                </h2>

                {course.description && (
                  <p className="mt-4 text-lg leading-relaxed text-slate-600">
                    {course.description}
                  </p>
                )}

                <div className="mt-8 space-y-7">
                  {version.modules.map(
                    (
                      module,
                      moduleIndex
                    ) => (
                      <section
                        key={module.id}
                        className="border-t border-slate-200 pt-7"
                      >
                        <p className="text-xs font-bold uppercase tracking-wide text-safety">
                          Module{" "}
                          {moduleIndex +
                            1}
                        </p>

                        <h3 className="mt-2 font-display text-2xl font-bold text-navy">
                          {module.title}
                        </h3>

                        <div className="mt-5 space-y-5">
                          {module.activities.map(
                            (
                              activity
                            ) => (
                              <article
                                key={
                                  activity.id
                                }
                                className="rounded-xl border border-slate-200 bg-slate-50 p-5"
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <h4 className="font-bold text-navy">
                                    {
                                      activity.title
                                    }
                                  </h4>

                                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                                    {activity.activity_type.replaceAll(
                                      "_",
                                      " "
                                    )}
                                  </span>
                                </div>

                                <div className="mt-4">
                                  <PreviewActivity
                                    activity={
                                      activity
                                    }
                                  />
                                </div>
                              </article>
                            )
                          )}
                        </div>
                      </section>
                    )
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid gap-8 xl:grid-cols-[340px_1fr]">
              <aside className="space-y-6">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <p className="label-eyebrow text-safety">
                    Course
                  </p>

                  <label className="mt-4 block text-sm font-semibold text-navy">
                    Title

                    <input
                      value={
                        courseForm.title
                      }
                      onChange={(event) =>
                        setCourseForm(
                          (current) => ({
                            ...current,
                            title:
                              event
                                .target
                                .value,
                          })
                        )
                      }
                      disabled={
                        course.status ===
                        "archived"
                      }
                      className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none focus:border-safety disabled:bg-slate-100"
                    />
                  </label>

                  <label className="mt-4 block text-sm font-semibold text-navy">
                    Description

                    <textarea
                      value={
                        courseForm.description
                      }
                      onChange={(event) =>
                        setCourseForm(
                          (current) => ({
                            ...current,
                            description:
                              event
                                .target
                                .value,
                          })
                        )
                      }
                      rows={5}
                      disabled={
                        course.status ===
                        "archived"
                      }
                      className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none focus:border-safety disabled:bg-slate-100"
                    />
                  </label>

                  <button
                    type="button"
                    onClick={saveCourse}
                    disabled={
                      busy ||
                      course.status ===
                        "archived"
                    }
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-navy px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" />
                    Save course details
                  </button>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <p className="label-eyebrow text-safety">
                    Version{" "}
                    {version.version_number}
                  </p>

                  <label className="mt-4 block text-sm font-semibold text-navy">
                    Pass mark (%)

                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={
                        versionForm.pass_mark
                      }
                      onChange={(event) =>
                        setVersionForm(
                          (current) => ({
                            ...current,
                            pass_mark:
                              event
                                .target
                                .value,
                          })
                        )
                      }
                      disabled={!editable}
                      className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 disabled:bg-slate-100"
                    />
                  </label>

                  <label className="mt-4 block text-sm font-semibold text-navy">
                    Estimated minutes

                    <input
                      type="number"
                      min="1"
                      value={
                        versionForm.estimated_minutes
                      }
                      onChange={(event) =>
                        setVersionForm(
                          (current) => ({
                            ...current,
                            estimated_minutes:
                              event
                                .target
                                .value,
                          })
                        )
                      }
                      disabled={!editable}
                      className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 disabled:bg-slate-100"
                    />
                  </label>

                  <label className="mt-4 block text-sm font-semibold text-navy">
                    CPD minutes

                    <input
                      type="number"
                      min="0"
                      value={
                        versionForm.cpd_minutes
                      }
                      onChange={(event) =>
                        setVersionForm(
                          (current) => ({
                            ...current,
                            cpd_minutes:
                              event
                                .target
                                .value,
                          })
                        )
                      }
                      disabled={!editable}
                      className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 disabled:bg-slate-100"
                    />
                  </label>

                  {editable ? (
                    <button
                      type="button"
                      onClick={saveVersion}
                      disabled={busy}
                      className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-navy px-4 py-2.5 text-sm font-bold text-navy disabled:opacity-50"
                    >
                      <Save className="h-4 w-4" />
                      Save version settings
                    </button>
                  ) : (
                    <div className="mt-4 flex gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs leading-relaxed text-emerald-800">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                      Published versions are read-only evidence records.
                    </div>
                  )}
                </div>
              </aside>

              <div>
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <p className="label-eyebrow text-safety">
                      Course structure
                    </p>

                    <h2 className="mt-2 font-display text-3xl font-bold text-navy">
                      Modules and activities
                    </h2>
                  </div>
                </div>

                {editable && (
                  <form
                    onSubmit={addModule}
                    className="mt-6 flex gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                  >
                    <input
                      value={
                        newModuleTitle
                      }
                      onChange={(event) =>
                        setNewModuleTitle(
                          event.target.value
                        )
                      }
                      placeholder="New module title"
                      className="min-w-0 flex-1 rounded-lg border border-slate-300 px-4 py-3 outline-none focus:border-safety"
                    />

                    <button
                      type="submit"
                      disabled={
                        busy ||
                        !newModuleTitle.trim()
                      }
                      className="inline-flex items-center gap-2 rounded-lg bg-navy px-5 py-3 text-sm font-bold text-white disabled:opacity-50"
                    >
                      <Plus className="h-4 w-4" />
                      Add module
                    </button>
                  </form>
                )}

                <div className="mt-6 space-y-5">
                  {version.modules.map(
                    (module, index) => (
                      <section
                        key={module.id}
                        className="rounded-2xl border border-slate-200 bg-white shadow-sm"
                      >
                        <div className="flex flex-col justify-between gap-4 border-b border-slate-200 p-5 md:flex-row md:items-center">
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wide text-safety">
                              Module{" "}
                              {index + 1}
                            </p>

                            <h3 className="mt-1 font-display text-2xl font-bold text-navy">
                              {module.title}
                            </h3>

                            <p className="mt-1 text-xs text-slate-500">
                              {
                                module
                                  .activities
                                  .length
                              }{" "}
                              activities ·{" "}
                              {module.required
                                ? "Required"
                                : "Optional"}
                            </p>
                          </div>

                          {editable && (
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  changeModulePosition(
                                    module,
                                    -1
                                  )
                                }
                                disabled={
                                  busy ||
                                  module.position <=
                                    1
                                }
                                className="rounded-lg border border-slate-300 p-2 text-slate-600 disabled:opacity-30"
                                title="Move up"
                              >
                                <ChevronUp className="h-4 w-4" />
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  changeModulePosition(
                                    module,
                                    1
                                  )
                                }
                                disabled={busy}
                                className="rounded-lg border border-slate-300 p-2 text-slate-600"
                                title="Move down"
                              >
                                <ChevronDown className="h-4 w-4" />
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  deleteModule(
                                    module
                                  )
                                }
                                disabled={busy}
                                className="rounded-lg border border-red-200 p-2 text-red-600"
                                title="Delete module"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="p-5">
                          <div className="space-y-3">
                            {module.activities.map(
                              (
                                activity
                              ) => (
                                <div
                                  key={
                                    activity.id
                                  }
                                  className="flex flex-col justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center"
                                >
                                  <div className="flex items-start gap-3">
                                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-navy shadow-sm">
                                      <BookOpen className="h-4 w-4" />
                                    </div>

                                    <div>
                                      <p className="font-bold text-navy">
                                        {
                                          activity.title
                                        }
                                      </p>

                                      <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                                        {activity.activity_type.replaceAll(
                                          "_",
                                          " "
                                        )}
                                      </p>
                                    </div>
                                  </div>

                                  {editable && (
                                    <div className="flex gap-2">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setActivityForm(
                                            activityToForm(
                                              module.id,
                                              activity
                                            )
                                          )
                                        }
                                        className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold text-navy"
                                      >
                                        Edit
                                      </button>

                                      <button
                                        type="button"
                                        onClick={() =>
                                          deleteActivity(
                                            module,
                                            activity
                                          )
                                        }
                                        className="rounded-lg border border-red-200 px-3 py-2 text-xs font-bold text-red-600"
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )
                            )}
                          </div>

                          {editable && (
                            <button
                              type="button"
                              onClick={() =>
                                setActivityForm(
                                  blankActivity(
                                    module.id
                                  )
                                )
                              }
                              className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-safety"
                            >
                              <Plus className="h-4 w-4" />
                              Add activity
                            </button>
                          )}
                        </div>
                      </section>
                    )
                  )}

                  {!version.modules
                    .length && (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
                      <BookOpen className="mx-auto h-10 w-10 text-slate-400" />

                      <h3 className="mt-4 font-display text-xl font-bold text-navy">
                        No modules yet
                      </h3>

                      <p className="mt-2 text-slate-600">
                        Add the first module to begin building this version.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {activityForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/60 p-4 md:p-8">
          <div className="mx-auto max-w-2xl rounded-2xl bg-white p-6 shadow-2xl md:p-8">
            <p className="label-eyebrow text-safety">
              {activityForm.activityId
                ? "Edit activity"
                : "New activity"}
            </p>

            <h2 className="mt-2 font-display text-3xl font-bold text-navy">
              Learning activity
            </h2>

            <form
              onSubmit={saveActivity}
              className="mt-6 space-y-5"
            >
              <label className="block text-sm font-semibold text-navy">
                Activity title

                <input
                  value={
                    activityForm.title
                  }
                  onChange={(event) =>
                    setActivityForm(
                      (current) => ({
                        ...current,
                        title:
                          event.target.value,
                      })
                    )
                  }
                  required
                  className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 outline-none focus:border-safety"
                />
              </label>

              <label className="block text-sm font-semibold text-navy">
                Activity type

                <select
                  value={
                    activityForm.activity_type
                  }
                  onChange={(event) =>
                    setActivityForm(
                      (current) => ({
                        ...current,
                        activity_type:
                          event.target.value,
                        main: "",
                        options: "",
                      })
                    )
                  }
                  className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 outline-none focus:border-safety"
                >
                  {ACTIVITY_TYPES.map(
                    ([value, label]) => (
                      <option
                        key={value}
                        value={value}
                      >
                        {label}
                      </option>
                    )
                  )}
                </select>
              </label>

              <label className="block text-sm font-semibold text-navy">
                {activityMainLabel(
                  activityForm.activity_type
                )}

                {activityForm.activity_type ===
                  "content" ||
                activityForm.activity_type ===
                  "scenario" ||
                activityForm.activity_type ===
                  "reflection" ? (
                  <textarea
                    value={
                      activityForm.main
                    }
                    onChange={(event) =>
                      setActivityForm(
                        (current) => ({
                          ...current,
                          main: event
                            .target.value,
                        })
                      )
                    }
                    required
                    rows={8}
                    className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 outline-none focus:border-safety"
                  />
                ) : (
                  <input
                    value={
                      activityForm.main
                    }
                    onChange={(event) =>
                      setActivityForm(
                        (current) => ({
                          ...current,
                          main: event
                            .target.value,
                        })
                      )
                    }
                    required
                    className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 outline-none focus:border-safety"
                  />
                )}
              </label>

              {needsOptions(
                activityForm.activity_type
              ) && (
                <label className="block text-sm font-semibold text-navy">
                  Options{" "}
                  <span className="font-normal text-slate-400">
                    one per line
                  </span>

                  <textarea
                    value={
                      activityForm.options
                    }
                    onChange={(event) =>
                      setActivityForm(
                        (current) => ({
                          ...current,
                          options:
                            event.target
                              .value,
                        })
                      )
                    }
                    required
                    rows={5}
                    className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 outline-none focus:border-safety"
                  />
                </label>
              )}

              <label className="flex items-center gap-3 text-sm font-semibold text-navy">
                <input
                  type="checkbox"
                  checked={
                    activityForm.required
                  }
                  onChange={(event) =>
                    setActivityForm(
                      (current) => ({
                        ...current,
                        required:
                          event.target
                            .checked,
                      })
                    )
                  }
                  className="h-4 w-4"
                />

                Required for completion
              </label>

              <div className="flex gap-3 border-t border-slate-200 pt-5">
                <button
                  type="submit"
                  disabled={busy}
                  className="inline-flex items-center gap-2 rounded-lg bg-navy px-5 py-3 text-sm font-bold text-white disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />

                  {activityForm.activityId
                    ? "Save activity"
                    : "Add activity"}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setActivityForm(null)
                  }
                  className="rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}